import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    addToCart,
    removeFromCart,
    fetchCart,
    clearCartApi,
} from "../../features/cartSlice";
import {
    Row,
    Col,
    Image,
    Table,
    Badge,
    Modal,
    Button,
    Form,               // ⬅️ add Form for checkboxes
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function CartDetails({ cartAmounts, cartCounts, totalDiscounts, setProceedNext, setCartTotalSavings }) {
    const dispatch = useDispatch();
    const { cartListNew, cartAmount, cartCount, totalDiscount, subTotal, loading } = useSelector((state) => state.cart);

    const [list, setList] = useState([]);
    const [outStockList, setOutStockList] = useState([]);
    const [groupedByCategory, setGroupedByCategory] = useState({});
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showClearModal, setShowClearModal] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [confirmIds, setConfirmIds] = useState([]); // array of productItemIds
    // NEW: selection state for multi-delete by productItemId
    const [selectedPids, setSelectedPids] = useState(new Set());
    const toggleSelect = (productItemId) => {
        setSelectedPids((prev) => {
            const next = new Set(prev);
            const id = Number(productItemId);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const openConfirmSingle = (pid) => {
        setConfirmIds([Number(pid)]);
        setShowConfirmRemove(true);
    };

    const openConfirmSelected = () => {
        const ids = Array.from(selectedPids);
        if (!ids.length) return;
        setConfirmIds(ids.map(Number));
        setShowConfirmRemove(true);
    };

    const confirmRemoveNow = async () => {
        setRemoving(true);
        try {
            await dispatch(clearCartApi({ productItemIds: confirmIds })).unwrap();
            // clean up selection locally
            setSelectedPids(prev => {
                const n = new Set(prev);
                confirmIds.forEach(id => n.delete(id));
                return n;
            });
            setShowConfirmRemove(false);
            // optional light refresh
            setTimeout(() => dispatch(fetchCart()), 200);
        } finally {
            setRemoving(false);
        }
    };
    const handleIncrement = (item, qty, amount) => {
        dispatch(addToCart({ productItemId: item, quantity: 1, price: amount }));
    };

    const handleDecrement = (item, qty, amount) => {
        if (qty <= 1) {
            setSelectedProduct({ productItemId: item, quantity: qty, price: amount });
            setShowRemoveModal(true); // confirm → API remove by productItemId
        } else {
            dispatch(removeFromCart({ productItemId: item, quantity: qty, price: amount }));
        }
    };

    const optimisticRemove = (productItemIds) => {
        // remove from screen immediately, no full reload
        const idSet = new Set(productItemIds.map(Number));
        // If your cart is also kept in Redux only, dispatch a reducer;
        // If it’s only from store, you can rely on the slice’s fulfilled handler.
        // Easiest: just re-fetch once after API returns. For instant feel, do both:
    };

    const handleRemoveByPid = async (productItemId) => {
        const ids = [Number(productItemId)];
        // optimistic
        // (option A) quick local feel: hide selection checkmark immediately
        setSelectedPids(prev => { const n = new Set(prev); n.delete(ids[0]); return n; });

        // (option B) if you also want rows gone immediately, dispatch a dedicated reducer
        // dispatch(removeItemsOptimistic({ productItemIds: ids }));

        try {
            await dispatch(clearCartApi({ productItemIds: ids })).unwrap();
            // optional sanity refresh
            setTimeout(() => dispatch(fetchCart()), 200);
        } catch {
            // rollback by refetching
            dispatch(fetchCart());
        }
    };

    const handleDeleteSelected = async () => {
        const ids = Array.from(selectedPids);
        if (!ids.length) return;
        setRemoving(true);
        // optimistic
        // dispatch(removeItemsOptimistic({ productItemIds: ids }));
        setSelectedPids(new Set());
        try {
            await dispatch(clearCartApi({ productItemIds: ids })).unwrap();
            setTimeout(() => dispatch(fetchCart()), 200);
        } finally {
            setRemoving(false);
        }
    };

    const handleClearAll = async () => {
        try {
            setClearing(true);
            await dispatch(clearCartApi({})).unwrap(); // clears ALL for current user/session
            setShowClearModal(false);
            setSelectedPids(new Set());
        } finally {
            setClearing(false);
        }
    };

    // Update totals on cart change
    useEffect(() => {
        cartAmounts(subTotal);
        cartCounts(cartCount);
        totalDiscounts(totalDiscount);
    }, [cartAmount, cartCount, totalDiscount, subTotal, cartAmounts, cartCounts, totalDiscounts]);

    // Calculate savings and group items by category
    useEffect(() => {
        if (cartListNew && cartListNew.length > 0) {
            if (setCartTotalSavings) {
                let savings = 0;
                cartListNew.forEach((item) => {
                    const mrp = parseFloat(item.mrpPrice) || 0;
                    const salePrice = parseFloat(item.price) || 0;
                    const qty = parseInt(item.totalQuantity) || 1;
                    if (mrp > salePrice) {
                        savings += (mrp - salePrice) * qty;
                    }
                });
                setCartTotalSavings(savings);
            }

            const outOfStockItems = cartListNew.filter((item) => item.stock === 0);
            const inStockItems = cartListNew.filter((item) => item.stock !== 0);

            const grouped = inStockItems.reduce((acc, item) => {
                const categoryId = item.categoryId || "other";
                const categoryName = item.categoryName || "Others";
                if (!acc[categoryId]) {
                    acc[categoryId] = { categoryId, categoryName, items: [] };
                }
                acc[categoryId].items.push(item);
                return acc;
            }, {});

            setGroupedByCategory(grouped);
            setOutStockList(outOfStockItems);
        }
    }, [cartListNew, setCartTotalSavings]);

    useEffect(() => {
        let stockEx = cartListNew.filter((e) => e.stock === 0);
        setProceedNext(stockEx.length === 0);
        setList([...cartListNew].sort((a, b) => a.stock - b.stock));
    }, [cartListNew, setProceedNext]);

    if (loading) {
        return (
            <div className="text-center my-5">
                <span className="spinner-border text-primary" role="status"></span>
                <p>Loading your cart...</p>
            </div>
        );
    }

    return (
        <>
            <div className="text-end mb-3">
                {cartListNew.length > 0 && (
                    <>
                        <Button
                            className="me-2"
                            variant="outline-danger text-white"
                            disabled={selectedPids.size === 0 || clearing}
                            onClick={openConfirmSelected}
                        >
                            {removing ? "Removing…" : "Remove selected"}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => setShowClearModal(true)}
                            disabled={loading || clearing}
                        >
                            {clearing ? "Clearing…" : "Clear All"}
                        </Button>
                    </>
                )}
            </div>

            {outStockList.length > 0 && (
                <div className="cartGroupDiv">
                    <h5 className="cartH5 text-danger">Out of Stock</h5>
                    <Table responsive className="cartTable">
                        <thead>
                            <tr>
                                <th className="col-1">#</th>
                                <th className="col-5">Products in cart</th>
                                <th className="col-2 text-center">Quantity</th>
                                <th className="col-2 text-end">Net Price</th>
                                <th className="col-2 text-end">Total Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {outStockList.map((cartProduct, index) => (
                                <tr key={`out-of-stock-${index}`}>
                                    <td className="align-middle">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-2"
                                            disabled={!cartProduct.productItemId}
                                            checked={selectedPids.has(Number(cartProduct.productItemId))}
                                            onChange={() => toggleSelect(cartProduct.productItemId)}
                                        />
                                        <FontAwesomeIcon
                                            icon="fa-solid fa-xmark"
                                            className="cursor-pointer ms-2"
                                            onClick={() => openConfirmSingle(cartProduct.productItemId)}
                                            style={{ color: "#919191" }}
                                        />
                                    </td>
                                    <td>
                                        <Row className="align-items-center">
                                            <Col xs={3} md={2}>
                                                <Image
                                                    src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${cartProduct.image}`}
                                                    rounded
                                                    className="CTimg"
                                                />
                                            </Col>
                                            <Col xs={9} md={10}>
                                                <span className="mb-0 cartTxt">
                                                    {cartProduct.productName}
                                                    <br />
                                                    {/* {cartProduct.badge && <Badge bg="success">{cartProduct.badge}</Badge>} */}
                                                    <Badge bg="danger">Out Of Stock</Badge>
                                                </span>
                                            </Col>
                                        </Row>
                                    </td>
                                    <td className="text-center">{cartProduct.totalQuantity}</td>
                                    <td className="text-end">
                                        {cartProduct.mrpPrice !== cartProduct.price && (
                                            <div className="text-decoration-line-through text-secondary">
                                                <FontAwesomeIcon icon="fa-solid fa-indian-rupee-sign" />
                                                &nbsp;{cartProduct.mrpPrice}
                                            </div>
                                        )}
                                        <div>
                                            <FontAwesomeIcon icon="fa-solid fa-indian-rupee-sign" />
                                            &nbsp;{cartProduct.price}
                                        </div>
                                    </td>
                                    <td className="text-end">
                                        <FontAwesomeIcon icon="fa-solid fa-indian-rupee-sign" />
                                        &nbsp;{(cartProduct.price * cartProduct.totalQuantity).toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {Object.values(groupedByCategory).map((group) => (
                <div key={group.categoryId} className="cartGroupDiv">
                    <h5 className="cartH5">{group.categoryName}</h5>
                    <Table responsive className="cartTable">
                        <thead>
                            <tr>
                                <th className="col-1">#</th>
                                <th className="col-5">Products in cart</th>
                                <th className="col-2 text-center">Quantity</th>
                                <th className="col-2 text-end">Net Price</th>
                                <th className="col-2 text-end">Total Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.items.map((cartProduct, index) => (
                                <tr key={`${group.categoryId}-${index}`}>
                                    <td className="align-middle">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-2"
                                            disabled={!cartProduct.productItemId}
                                            checked={selectedPids.has(Number(cartProduct.productItemId))}
                                            onChange={() => toggleSelect(cartProduct.productItemId)}
                                        />
                                        {cartProduct.productItemId && (
                                            <FontAwesomeIcon
                                                icon="fa-solid fa-xmark"
                                                className="cursor-pointer ms-2"
                                                onClick={() => openConfirmSingle(cartProduct.productItemId)}
                                                style={{ color: "#919191" }}
                                            />
                                        )}
                                    </td>
                                    <td>
                                        <Row className="align-items-center">
                                            <Col xs={3} md={2}>
                                                <Image
                                                    src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${cartProduct.image}`}
                                                    rounded
                                                    className="CTimg"
                                                />
                                            </Col>
                                            <Col xs={9} md={10}>
                                                <span className="mb-0 cartTxt">
                                                    {cartProduct.productName}
                                                    <br />
                                                    {/* <Badge bg="success">
                                                        {cartProduct.badge && <div>{cartProduct.badge}</div>}
                                                    </Badge> */}
                                                    {cartProduct.stock === 0 && <Badge bg="danger">Out Of Stock</Badge>}
                                                </span>
                                            </Col>
                                        </Row>
                                    </td>
                                    <td>
                                        <div className="input-group justify-content-center Cvarient mt-1">
                                            {cartProduct.productItemId && (
                                                <div className="input-group-prepend cursor-pointer ">
                                                    <span
                                                        className="input-group-text"
                                                        onClick={() =>
                                                            handleDecrement(
                                                                cartProduct.productItemId,
                                                                cartProduct.totalQuantity,
                                                                cartProduct.price
                                                            )
                                                        }
                                                    >
                                                        -
                                                    </span>
                                                </div>
                                            )}
                                            <input
                                                type="text"
                                                className="form-control p-1 text-center"
                                                value={cartProduct.totalQuantity}
                                                disabled
                                            />
                                            {cartProduct.productItemId && (
                                                <div className="input-group-append cursor-pointer">
                                                    <span
                                                        className="input-group-text"
                                                        onClick={() =>
                                                            handleIncrement(
                                                                cartProduct.productItemId,
                                                                cartProduct.totalQuantity,
                                                                cartProduct.price
                                                            )
                                                        }
                                                    >
                                                        +
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-end">
                                        {cartProduct.mrpPrice !== cartProduct.price && (
                                            <div className="w-100 cartMrpPrice mt-2 text-secondary text-decoration-line-through">
                                                <FontAwesomeIcon icon="fa-solid fa-indian-rupee-sign" size="sm" />
                                                &nbsp;{cartProduct.mrpPrice}
                                            </div>
                                        )}
                                        <div className="w-100 cartPrice mt-2">
                                            <FontAwesomeIcon icon="fa-solid fa-indian-rupee-sign" size="sm" />
                                            &nbsp;{cartProduct.price}
                                        </div>
                                    </td>
                                    <td className="text-end">
                                        <div className="w-100 cartPrice mt-2">
                                            <FontAwesomeIcon icon="fa-solid fa-indian-rupee-sign" size="sm" />
                                            &nbsp;
                                            {(cartProduct.price * cartProduct.totalQuantity).toFixed(2)}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ))}

            {/* Confirm remove single when qty would hit 0 */}
            <Modal
                show={showRemoveModal}
                onHide={() => setShowRemoveModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Remove Product</Modal.Title>
                </Modal.Header>
                <Modal.Body>Do you want to remove the product from the cart?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={async () => {
                            if (selectedProduct?.productItemId) {
                                await handleRemoveByPid(selectedProduct.productItemId);
                                setShowRemoveModal(false);
                                setSelectedProduct(null);
                            }
                        }}
                    >
                        Remove
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Clear All */}
            <Modal
                show={showClearModal}
                onHide={() => setShowClearModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Clear Cart</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to clear all items from the cart?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowClearModal(false)} disabled={clearing}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleClearAll} disabled={clearing}>
                        {clearing ? "Clearing…" : "Clear All"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showConfirmRemove}
                onHide={() => setShowConfirmRemove(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm removal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {confirmIds.length === 1
                        ? "Do you want to remove this item from the cart?"
                        : `Do you want to remove ${confirmIds.length} selected items from the cart?`}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowConfirmRemove(false)}
                        disabled={removing}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmRemoveNow} disabled={removing}>
                        {removing ? "Removing…" : "Remove"}
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
}

export default CartDetails;
